<template>
  <div class="manual-bank-form">
    <div class="mb-4">
      <div class="md:flex justify-between mb-3">
        <div class="flex-initial">
          <label for="routingNumber">Routing Number</label>
        </div>
        <div
          v-if="getError('routing_number')"
          class="flex-initial mt-2 sm:mt-0 md:ml-2 font-semibold text-red-500"
        >
          {{ getError('routing_number') }}
        </div>
        <div
          v-else-if="routingLookupValid === false && !routingLookupLoading"
          class="flex-initial mt-2 sm:mt-0 md:ml-2 font-semibold text-red-500"
        >
          We couldn't identify this routing number. Please double-check it.
        </div>
      </div>
      <div class="relative">
        <input
          type="text"
          id="routingNumber"
          name="routing_number"
          placeholder="9-digit routing number"
          class="input-field w-full"
          :class="routingInputClass"
          maxlength="9"
          inputmode="numeric"
          v-model="formData.routingNumber"
          @input="onRoutingNumberInput($event.target.value)"
        />
        <div v-if="routingLookupLoading" class="absolute inset-y-0 right-3 flex items-center pointer-events-none">
          <svg class="animate-spin w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
          </svg>
        </div>
      </div>
    </div>

    <div class="mb-4">
      <div class="md:flex justify-between mb-3">
        <div class="flex-initial">
          <label :for="(routingLookupNeedsManualBankName || routingLookupErrored) ? 'officialName' : undefined">Name of Bank</label>
        </div>
        <div
          v-if="getError('official_name')"
          class="flex-initial mt-2 sm:mt-0 md:ml-2 font-semibold text-red-500"
        >
          {{ getError('official_name') }}
        </div>
      </div>
      <!-- Editable fallback when lookup returns valid-but-unknown or throws -->
      <template v-if="routingLookupNeedsManualBankName || routingLookupErrored">
        <input
          type="text"
          id="officialName"
          name="official_name"
          placeholder="Enter your bank name"
          class="input-field w-full"
          v-model="formData.officialName"
        />
        <p v-if="routingLookupErrored" class="mt-1 text-sm text-yellow-700">
          Lookup unavailable — please enter your bank name manually.
        </p>
      </template>
      <!-- Happy path: read-only auto-filled display -->
      <template v-else>
        <div :class="bankNameDisplayClass">
          <span v-if="routingLookupLoading" class="text-gray-400 italic">Looking up bank...</span>
          <span v-else-if="formData.officialName" class="flex items-center gap-2">
            <svg class="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
            {{ formData.officialName }}
          </span>
          <span v-else class="text-gray-400 italic">Determined by routing number</span>
        </div>
      </template>
    </div>

    <div class="mb-4">
      <div class="md:flex justify-between mb-3">
        <div class="flex-initial">
          <label for="accountNumber">Account Number</label>
        </div>
        <div
          v-if="getError('account_number')"
          class="flex-initial mt-2 sm:mt-0 md:ml-2 font-semibold text-red-500"
        >
          {{ getError('account_number') }}
        </div>
      </div>
      <div class="md:flex gap-2">
        <input
          type="text"
          id="accountNumber"
          name="account_number"
          placeholder="Account Number"
          class="input-field w-full"
          v-model="formData.accountNumber"
        />
        <input
          type="text"
          name="account_number_confirmation"
          placeholder="Confirm Account Number"
          class="input-field w-full mt-2 md:mt-0"
          v-model="formData.accountNumberConfirmation"
        />
      </div>
    </div>

    <div class="flex justify-center items-center gap-4 mt-8">
      <button
        @click="$emit('cancel')"
        class="px-6 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
      >
        Cancel
      </button>
      <AppButton
        :loading="loading"
        :style="'solid'"
        @click="submitForm"
      >
        Submit
      </AppButton>
    </div>
  </div>
</template>

<script>
import AppButton from './AppButton';

export default {
  name: 'ManualBankAccountForm',
  components: {
    AppButton,
  },
  props: {
    loan: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      formData: {
        officialName: '',
        accountNumber: '',
        accountNumberConfirmation: '',
        routingNumber: '',
      },
      loading: false,
      errors: {},
      routingLookupLoading: false,
      // null = not yet checked, true = passes ABA checksum, false = fails ABA checksum
      routingLookupValid: null,
      // true when lookup passed validation but did not return a bank name
      routingLookupNeedsManualBankName: false,
      // true when the lookup API request itself failed (network/server error)
      routingLookupErrored: false,
    };
  },
  computed: {
    routingInputClass() {
      if (this.getError('routing_number') || this.routingLookupValid === false) {
        return 'border-red-400';
      }
      if (this.routingLookupValid === true) {
        return 'border-green-400';
      }
      return '';
    },
    bankNameDisplayClass() {
      const base = 'w-full p-3 border rounded-lg min-h-[48px] flex items-center';
      if (this.formData.officialName) return `${base} border-green-400 bg-green-50`;
      return `${base} border-gray-300 bg-gray-50`;
    },
  },
  methods: {
    getError(errorName) {
      return this.errors && this.errors[errorName] && this.errors[errorName].join(', ');
    },
    async onRoutingNumberInput(val) {
      if (val.length !== 9) {
        this.routingLookupValid = null;
        this.routingLookupNeedsManualBankName = false;
        this.routingLookupLoading = false;
        this.routingLookupErrored = false;
        this.formData.officialName = '';
        return;
      }

      await this.lookupRoutingNumber(val);
    },
    async lookupRoutingNumber(routingNumber) {
      this.routingLookupLoading = true;
      this.routingLookupValid = null;
      this.routingLookupNeedsManualBankName = false;
      this.routingLookupErrored = false;

      try {
        const { data } = await this.$http.get('/lg-routing-number-lookup', {
          params: { routing: routingNumber },
        });

        if (data.valid) {
          this.routingLookupValid = true;
          this.routingLookupNeedsManualBankName = !data.bank_name;
          this.formData.officialName = data.bank_name || '';
        } else {
          this.routingLookupValid = false;
          this.routingLookupNeedsManualBankName = false;
          this.formData.officialName = '';
        }
      } catch (e) {
        this.routingLookupErrored = true;
        this.routingLookupValid = null;
        this.routingLookupNeedsManualBankName = false;
        this.formData.officialName = '';
      } finally {
        this.routingLookupLoading = false;
      }
    },
    submitForm() {
      this.loading = true;
      this.errors = {};

      const formData = new FormData();
      formData.append('official_name', this.formData.officialName);
      formData.append('account_number', this.formData.accountNumber);
      formData.append('account_number_confirmation', this.formData.accountNumberConfirmation);
      formData.append('routing_number', this.formData.routingNumber);

      formData.append('is_changing_bank_flow', false);
      formData.append('onboarding_attempt', true);

      this.$http
        .post(`/signup/light-mode-income-verification/${this.loan.id}/manual`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(
          () => {
            this.loading = false;
            window.location.reload();
          },
          (error) => {
            this.loading = false;
            if (error.response?.status === 413) {
              this.errors = {
                bank_statement: ['Please upload a smaller file, up to 20MB'],
              };
            } else if (error.response?.data?.errors) {
              this.errors = error.response.data.errors;
            }
          }
        );
    },
  },
};
</script>

<style scoped>
.input-field {
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.input-field:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

label {
  font-weight: 600;
  color: #374151;
}
</style>
